/**
 * Loans Filter Panel Component
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SearchInput } from '../common/SearchInput';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { CalendarIcon, Download, Filter, X } from 'lucide-react';
import { formatDate } from '../../lib/formatters';

// Status filter options. Values mirror the backend LoanStatus enum
// (app/models/loan.py / app/schemas/loan.py) exactly so each option filters the
// stored status correctly. 'Frozen' is included because the Loans table shows it
// (derived from loan.is_frozen); the backend status filter special-cases
// status=Frozen -> is_frozen == True so this option returns the right rows.
const LOAN_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Sanctioned', label: 'Sanctioned' },
  { value: 'Partially Disbursed', label: 'Partially Disbursed' },
  { value: 'Disbursed', label: 'Disbursed' },
  { value: 'Active', label: 'Active' },
  { value: 'Frozen', label: 'Frozen' },
  { value: 'Closure Requested', label: 'Closure Requested' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Foreclosed', label: 'Foreclosed' },
  { value: 'Written Off', label: 'Written Off' },
  { value: 'Cancelled', label: 'Cancelled' },
];

export function LoansFilters({
  searchTerm, setSearchTerm, statusFilter, setStatusFilter,
  loanTypeFilter, setLoanTypeFilter, dateFrom, setDateFrom,
  dateTo, setDateTo, minAmount, setMinAmount, maxAmount, setMaxAmount,
  showFilters, setShowFilters, activeFiltersCount, onClearFilters, products,
  onExport, exporting = false,
}) {
  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center gap-2">
        <SearchInput
          className="max-w-none flex-1"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by loan number or borrower"
          testId="loans-search-input"
        />
        <Button
          variant={activeFiltersCount > 0 ? "default" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="loans-filter-toggle"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFiltersCount > 0 && (
            <span className="ml-2 inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1.5 text-xs font-semibold leading-none text-primary-foreground">
              {activeFiltersCount}
            </span>
          )}
        </Button>
        {activeFiltersCount > 0 && (
          <Button variant="ghost" onClick={onClearFilters} data-testid="loans-clear-filters">
            <X className="h-4 w-4 mr-2" />Clear
          </Button>
        )}
        {onExport && (
          <Button variant="outline" onClick={onExport} disabled={exporting} data-testid="loans-export-button">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        )}
      </div>

      {showFilters && (
        <AdvancedFiltersPanel
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          loanTypeFilter={loanTypeFilter} setLoanTypeFilter={setLoanTypeFilter}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          minAmount={minAmount} setMinAmount={setMinAmount}
          maxAmount={maxAmount} setMaxAmount={setMaxAmount}
          products={products}
        />
      )}
    </div>
  );
}

function AdvancedFiltersPanel({
  statusFilter, setStatusFilter, loanTypeFilter, setLoanTypeFilter,
  dateFrom, setDateFrom, dateTo, setDateTo,
  minAmount, setMinAmount, maxAmount, setMaxAmount, products,
}) {
  return (
    <div className="p-4 border rounded-lg bg-card space-y-4" data-testid="loans-filter-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="loans-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOAN_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Loan Type</Label>
          <Select value={loanTypeFilter} onValueChange={setLoanTypeFilter}>
            <SelectTrigger data-testid="loans-loan-type-filter">
              <SelectValue placeholder="All Loan Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Loan Types</SelectItem>
              {products.map((product) => (
                <SelectItem key={product.id} value={String(product.id)}>
                  {product.product_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Date From</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFrom ? formatDate(dateFrom) : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Date To</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateTo ? formatDate(dateTo) : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} disabled={dateFrom ? (d) => d < dateFrom : undefined} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Min Amount</Label>
          <Input
            type="number"
            placeholder="0"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            data-testid="loans-min-amount-filter"
          />
        </div>
        <div className="space-y-2">
          <Label>Max Amount</Label>
          <Input
            type="number"
            placeholder="Unlimited"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            data-testid="loans-max-amount-filter"
          />
        </div>
      </div>
    </div>
  );
}

LoansFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  loanTypeFilter: PropTypes.string,
  setLoanTypeFilter: PropTypes.func.isRequired,
  dateFrom: PropTypes.instanceOf(Date),
  setDateFrom: PropTypes.func.isRequired,
  dateTo: PropTypes.instanceOf(Date),
  setDateTo: PropTypes.func.isRequired,
  minAmount: PropTypes.string,
  setMinAmount: PropTypes.func.isRequired,
  maxAmount: PropTypes.string,
  setMaxAmount: PropTypes.func.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  activeFiltersCount: PropTypes.number.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    product_name: PropTypes.string.isRequired,
  })).isRequired,
  onExport: PropTypes.func,
  exporting: PropTypes.bool,
};

AdvancedFiltersPanel.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  loanTypeFilter: PropTypes.string,
  setLoanTypeFilter: PropTypes.func.isRequired,
  dateFrom: PropTypes.instanceOf(Date),
  setDateFrom: PropTypes.func.isRequired,
  dateTo: PropTypes.instanceOf(Date),
  setDateTo: PropTypes.func.isRequired,
  minAmount: PropTypes.string,
  setMinAmount: PropTypes.func.isRequired,
  maxAmount: PropTypes.string,
  setMaxAmount: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    product_name: PropTypes.string.isRequired,
  })).isRequired,
};
